/**
 * Enterprise projects: owned by the organisation, with members and roles,
 * and a state of record that is not on anyone's laptop — the canonical
 * clone of each root lives on a project node, and members attach to it
 * (mju-ai-workbench docs/plans/2026-09-agent-nodes.md, "Enterprise projects").
 *
 *   GET|POST /admin/v1/projects, PATCH|DELETE /admin/v1/projects/:id      administrators
 *   GET  /projects/v1                        the caller's projects (every project for an administrator);
 *                                            a person's token or a desktop's device token
 *   GET  /projects/v1/:id                    project, members, roots, compute, the caller's role; same tokens; and
 *                                            `roadmapPurpose`, the roadmap vision's first line (AIOE 0.76.0)
 *   PATCH /projects/v1/:id                   owners: the name, the classification, the purpose (0.76.0: one line of
 *                                            at most 240 characters; "" clears it, and the roadmap's line shows again)
 *   PUT  /projects/v1/:id/members            owners: the whole member list
 *   PUT  /projects/v1/:id/roots/:rootId      owners: which node holds the canonical clone
 *   GET  /projects/v1/:id/activity           members: audit rows with principal and actor
 *
 * The project id is the workbench's `prj_…` id from `.mojoup/project.json`.
 *
 * A merge, as a workbench sees it (AIOE 0.36.0, all optional, no version bump; workbench D250/D248):
 *   POST /projects/v1 with the id of a merged-away project answers 200 with the survivor plus `mergedFrom`
 *     (the id registered) and `rootMap` ({ oldRootId: newRootId }, only when roots were renamed);
 *     `projectRegisterAnswerSchema`.
 *   GET /projects/v1/:oldId answers 410 `{ error: "merged", mergedInto, mergedIntoName?, name?, mergedBy?, owner?, message }`:
 *     the survivor's id and name, the merged project's own name, who merged it and the survivor's first owner
 *     (`{ id, name }` each, names through the people directory); `projectMergedGoneSchema`.
 *   POST /projects/v1 by someone not on the survivor answers 409 `{ error: "not_a_member", mergedInto,
 *     mergedIntoName, owner?, message }`: ask `owner`; `projectNotAMemberSchema`.
 *   A desktop that is idle learns of a merge from the heartbeat's `merged` list (fleet.ts).
 */
import { z } from "zod";
import { pullRequestsPolicySchema } from "./pullRequests.js";
import { memoryMirrorSchema } from "./projectMemory.js";
import { cloudPlacementSchema } from "./cloudAgents.js";
import { linkedIdentitiesSchema } from "./people.js";
/*
 * Mirrorable (AIOE 0.21.0): this file imports nothing but zod, pullRequests.ts, projectMemory.ts
 * (and its memory.ts), cloudAgents.ts and people.ts (zod only), so the workbench can copy it as it is. A project's
 * root (`projectRootSchema`, `ProjectRoot`, `projectRootPutSchema`) and `workspaceHostDown()`,
 * which lean on fleet.ts's host states, live in projectRoots.ts; the governed-settings block is
 * written out below rather than imported from settingsPolicy.ts.
 */
export const projectRoleSchema = z.enum(["owner", "contributor", "viewer"]);
export const projectIdSchema = z.string().min(1).max(200).regex(/^[A-Za-z0-9_.:-]+$/, "letters, digits and _ . : -");
export const projectMemberSchema = z.object({
    subjectType: z.enum(["user", "group"]),
    /** Entra object id of a person, or an IdP group id. */
    subjectId: z.string().min(1).max(200),
    role: projectRoleSchema,
    /** For display: the person's name and email as their sign-in gave them (people.ts). Set on every answer, never taken from a request. */
    name: z.string().max(300).optional(),
    email: z.string().optional(),
    /** Optional, no version bump (D243): the address of `GET /people/v1/:id/picture` (people.ts), set on every answer for a person who may have one, never taken from a request. A client fetches it with its token and shows initials on a 404. */
    picture: z.string().url().max(2000).optional(),
    /** For display and assignment: the accounts the person linked, by name only (people.ts `linkedIdentitiesSchema`). Set on every answer for a person who linked any, never taken from a request. */
    linked: linkedIdentitiesSchema.optional(),
});
/**
 * The governed-settings block, the same shape as settingsPolicy.ts's `settingsPolicySchema`
 * (settings by key, and how each governs: locked or default), written out so this file needs
 * no settingsPolicy.ts; projectRoots.ts checks at compile time that the two stay the same.
 */
const projectSettingKeySchema = z.string().min(1).max(200).regex(/^[A-Za-z0-9_.-]+$/);
const projectSettingsModeSchema = z.enum(["locked", "default"]);
const projectSettingsPolicySchema = z.object({
    settings: z.record(projectSettingKeySchema, z.unknown()).default({}),
    settingsMode: z.record(projectSettingKeySchema, projectSettingsModeSchema).default({}),
});
/**
 * Every project on a platform organisation is the organisation's (`enterprise`): registered from a
 * workbench or made in the console, owned by its members, governed by the organisation's policy,
 * its memory, boards, roadmap, risks, runs and inbox hosted by the platform. Truly personal work
 * lives in the workbench's Personal space, not on a platform. `personal` is the value platforms
 * before 0.9.0 gave a project started from a workbench; it stays here so a workbench can read
 * them, and no platform issues it any more
 * (docs/plans/2026-10-organisation-projects-and-working-together.md).
 */
export const projectKindSchema = z.enum(["enterprise", "personal"]);
/**
 * Where a project is in its life (AIOE 0.31.0). `active` and `stale` (no machine has reported one of its
 * repositories for a month) are the live states; `archived` is read-only and hidden from pickers and the
 * workbench's list until an owner restores it; `merged` is a redirect to the project it was merged into
 * (`mergedInto`), never listed. The platform sets `state` on every answer; it is never taken from a request.
 */
export const projectStateSchema = z.enum(["active", "stale", "archived", "merged"]);
/**
 * How sensitive a project's data is (AIOE 0.40.0, optional, no version bump; `restricted` added in 0.61.0). It decides
 * which providers and models the work may use (the assurance register, AIOE `GET /projects/v1/:id/assurance`), and it
 * is the project's sensitivity mark the workbench reads (D321): a floor for every session in the project. Since Nov 2026
 * it is also the project's memory level (memory.ts `projectMemoryLevel`): the label of a project's memory item nothing
 * else labels, and the grant every member holds, so members read their project's memory up to its classification; a
 * project without one counts as Internal and its owners are asked to set one. A project's owner sets it; a
 * repository (root) inherits the project's and may only be stricter: `PUT /projects/v1/:id/roots/:rootId/classification`
 * answers 400 `classification_weaker` for a root marked less strict than its project.
 */
export const classificationSchema = z.enum(["public", "internal", "confidential", "restricted"]);
export const CLASSIFICATION_WORDS = {
    public: "Public or synthetic",
    internal: "Internal",
    confidential: "Confidential or regulated",
    restricted: "Restricted",
};
export const CLASSIFICATION_DESCRIPTIONS = {
    public: "Nothing here would matter if it leaked: open material, or made-up data.",
    internal: "Ordinary company work that is not for the public but would not harm anyone if it got out.",
    confidential: "Customer, personal, commercial or regulated information that must stay under the organisation's control.",
    restricted: "Information whose exposure would do serious harm (legal privilege, security, health, national security): it stays in the country or on the organisation's own machines.",
};
/** Strictest last, so a comparison is an index. */
export const CLASSIFICATION_ORDER = ["public", "internal", "confidential", "restricted"];
/** True when `a` is less strict than `b`. */
export const classificationWeaker = (a, b) => CLASSIFICATION_ORDER.indexOf(a) < CLASSIFICATION_ORDER.indexOf(b);
/** A project's purpose at most (the overview card's line, projectOverview.ts). */
export const PROJECT_PURPOSE_MAX = 240;
export const projectSchema = z.object({
    id: projectIdSchema,
    name: z.string().min(1).max(200),
    ownerType: z.enum(["org"]).default("org"),
    kind: projectKindSchema.default("enterprise"),
    /** The business area the project is filed under; unset until a portfolio manager or area manager files it. */
    businessAreaId: z.string().optional(),
    /** The person who started it from their workbench (object id): its first owner. Unset for a project made in the console. */
    ownerId: z.string().optional(),
    createdBy: z.string(),
    createdAt: z.string(),
    policyId: z.string().optional(),
    defaultTeamCatalogueId: z.string().optional(),
    /** Members may run teams and specialists of their own on the project (the policy's `teams.allowPersonal`). */
    allowPersonalTeams: z.boolean().default(true),
    /** The merge rules; unset means the defaults. */
    pullRequestPolicy: pullRequestsPolicySchema.optional(),
    /** The project's governed settings, layered over the tenant's. */
    settingsPolicy: projectSettingsPolicySchema.optional(),
    /** Whether the project's memory is mirrored into the organisation's index. */
    memoryMirror: memoryMirrorSchema.optional(),
    /** AIOE 0.33.0, optional: minutes past its lease before a task claim lapses and is released (15 to 1440; 0 never; unset 60). See coordinator.ts. */
    claimLapseMinutes: z.number().int().min(0).max(1440).optional(),
    /** Cost round 2 (AIOE 0.18.0, optional): the finance cost code its spend is booked to; beats its business area's on the statement. */
    costCode: z.string().max(64).optional(),
    /** AIOE 0.40.0, optional: how sensitive the project's data is; see `classificationSchema`. Unset means unclassified. */
    classification: classificationSchema.optional(),
    /** Lifecycle (0.31.0, optional): see `projectStateSchema`. Absent on a platform before 0.31.0 means active. */
    state: projectStateSchema.optional(),
    /** The project this one was merged into; set only when `state` is `merged`. */
    mergedInto: projectIdSchema.optional(),
    /** AIOE 0.36.0, optional: the principal who merged it away; set only when `state` is `merged`. */
    mergedBy: z.string().optional(),
    /** When and by whom (object id) the project was archived; set while it is archived. */
    archivedAt: z.string().optional(),
    archivedBy: z.string().optional(),
    /** AIOE 0.76.0, optional: what the project is for, in a line an owner wrote. Unset: the roadmap vision's first line stands in. */
    purpose: z.string().max(PROJECT_PURPOSE_MAX).optional(),
});
/** A person named: the id and what they are called (their name, else email, else the id). */
export const projectPersonSchema = z.object({ id: z.string(), name: z.string() });
/** `POST /projects/v1` answer for the id of a merged-away project: the survivor, with where it came from. */
export const projectRegisterAnswerSchema = projectSchema.extend({
    role: projectRoleSchema.optional(),
    /** The id that was registered, when it was a merged-away project's. */
    mergedFrom: projectIdSchema.optional(),
    /** { oldRootId: newRootId } for the roots the merge renamed; absent when none were. */
    rootMap: z.record(z.string(), z.string()).optional(),
});
/** `GET /projects/v1/:id` for a merged-away project: 410. */
export const projectMergedGoneSchema = z.object({
    error: z.literal("merged"),
    mergedInto: projectIdSchema,
    mergedIntoName: z.string().optional(),
    /** The merged-away project's own name. */
    name: z.string().optional(),
    /** Who merged it. Absent for a merge before 0.36.0. */
    mergedBy: projectPersonSchema.optional(),
    /** The survivor's first owner: the person to ask for a place on it. */
    owner: projectPersonSchema.optional(),
    message: z.string(),
});
/** `POST /projects/v1` 409: the project it was merged into exists and the caller is not on it. */
export const projectNotAMemberSchema = z.object({
    error: z.literal("not_a_member"),
    mergedInto: projectIdSchema,
    mergedIntoName: z.string(),
    owner: projectPersonSchema.optional(),
    message: z.string(),
});
/** `PUT /projects/v1/:id/settings`, owners: what the project allows and governs. */
export const projectSettingsPutSchema = z.object({
    allowPersonalTeams: z.boolean().optional(),
    /** Owners: 0 (never lapse), or 15 to 1440 minutes; the default is 60. */
    claimLapseMinutes: z.number().int().refine((n) => n === 0 || (n >= 15 && n <= 1440), "0 (never) or 15 to 1440 minutes").optional(),
    pullRequests: pullRequestsPolicySchema.optional(),
    settings: z.record(z.string(), z.unknown()).optional(),
    settingsMode: z.record(z.string(), projectSettingsModeSchema).optional(),
});
export const projectCreateSchema = z.object({
    /**
     * Left out, the platform mints one (`prj_` and twelve characters, as a workbench does): a workbench links to the
     * project when its person opens it from the organisation's list. Given, it is the id an existing workbench project
     * keeps in `.mojoup/project.json`, for filing that project before its workbench first registers it.
     */
    id: projectIdSchema.optional(),
    name: z.string().min(1).max(200),
    policyId: z.string().min(1).max(100).optional(),
    defaultTeamCatalogueId: z.string().min(1).max(100).optional(),
    /** The business area it sits in. */
    businessAreaId: z.string().min(1).max(100).optional(),
    /** People who own it from the start (object ids). */
    owners: z.array(z.string().min(1).max(200)).max(50).default([]),
    /** Directory groups whose members own it from the start (Entra object ids; not on Mojo Up AI Cloud). */
    ownerGroups: z.array(z.string().min(1).max(200)).max(50).default([]),
    /** The finance cost code (cost round 2); on a PATCH, "" clears it. */
    costCode: z.string().max(64).regex(/^[^\n\r]*$/, "one line").optional(),
    /**
     * Required (Nov 2026): how sensitive the project's data is. It decides what its members read of its memory and the
     * label of an item nothing else labels (`projectMemoryLevel`). A request without it answers 400 `classification_required`.
     */
    classification: classificationSchema.optional(),
});
export const projectPatchSchema = projectCreateSchema.omit({ id: true, owners: true, ownerGroups: true }).partial();
/** The 400 answer for an administrator's or a portfolio's create without a classification. */
export const CLASSIFICATION_REQUIRED = "classification_required";
/**
 * The Inbox action that sets a project's classification from the record asking its owners for one
 * (`POST /projects/v1/:id/notifications/:nid/act`), with `params: { classification }`. Owners only.
 */
export const PROJECT_CLASSIFY_RPC = "aioe.project.classify";
/** The dedupe key of that record: one per project, filed once. */
export const projectClassificationKey = (projectId) => `project-classification:${projectId}`;
/**
 * `POST /projects/v1`: a workbench (device token) or a person registers a project with the
 * organisation. The id is the workbench's `prj_…` from `.mojoup/project.json`; the caller is its
 * first owner and, until they add members, its only one. Registering a project the caller already
 * belongs to answers 200 and merges the roots sent; another person's answers 409.
 */
export const projectRegisterSchema = z.object({
    id: projectIdSchema,
    name: z.string().min(1).max(200),
    /** The project's roots as the workbench knows them, so the board and the console can name them. */
    roots: z
        .array(z.object({ rootId: projectIdSchema, remote: z.string().max(500).optional(), branch: z.string().max(200).optional() }))
        .max(50)
        .default([]),
    /** Register a new project although another one in the organisation has the same repository (the 409 `duplicate_of` answer). */
    createAnyway: z.boolean().optional(),
    /**
     * Additive (Nov 2026): the new project's classification, taken only when the project is new. A workbench that sends
     * none still registers it: the project is left unclassified, its owners are asked for one in the Inbox and the
     * console, and its memory counts as Internal until they answer.
     */
    classification: classificationSchema.optional(),
});
/**
 * `POST /projects/v1` answers 409 `duplicate_of` when a new project's repository (a root's `remote`,
 * compared as source.ts's `normaliseRemote()` writes it) is one that a project in the organisation already has and
 * the caller is not a member of. The caller asks to join (`POST /projects/v1/:id/join-request`) or
 * registers again with `createAnyway: true`.
 */
export const projectDuplicateSchema = z.object({
    error: z.literal("duplicate_of"),
    project: z.object({ id: projectIdSchema, name: z.string(), owners: z.array(z.string()) }),
    message: z.string(),
});
/** `POST /projects/v1/:id/join-request`, a person or a desktop: ask the owners to add the caller as a contributor. */
export const projectJoinRequestSchema = z.object({ note: z.string().max(500).optional() });
/** `POST /projects/v1/:id/merge`: propose merging this project (the source) into another. `?dryRun=1` answers the counts only. */
export const projectMergeProposeSchema = z.object({
    into: projectIdSchema,
    /** An administrator merges at once, without the target's owners' approval. */
    force: z.boolean().optional(),
    note: z.string().max(500).optional(),
});
/** `POST /projects/v1/:id/transfer`: make a person the owner; the previous owner stays as a contributor. */
export const projectTransferSchema = z.object({ to: z.string().min(1).max(200) });
/** A request waiting on a project's owners: to join it, or to merge another project into it. */
export const projectRequestSchema = z.object({
    id: z.string(),
    kind: z.enum(["join", "merge"]),
    /** The project the owners own: the one to join, or the one merged into. */
    projectId: projectIdSchema,
    /** A merge's source project. */
    sourceId: projectIdSchema.optional(),
    status: z.enum(["pending", "approved", "declined", "withdrawn"]),
    requestedBy: z.string(),
    requestedByName: z.string().optional(),
    requestedAt: z.string(),
    note: z.string().max(500).optional(),
    decidedBy: z.string().optional(),
    decidedAt: z.string().optional(),
    reason: z.string().max(2000).optional(),
});
/** What a merge moves, counted: the dry run's answer and the real one's. */
export const projectMergeSummarySchema = z.object({
    roots: z.number(),
    members: z.number(),
    boards: z.number(),
    tasks: z.number(),
    /** Tasks whose id was taken on the same root and board, so they got a new number. */
    renumbered: z.number(),
    records: z.number(),
    memory: z.number(),
    runs: z.number(),
    risks: z.number(),
    pictures: z.number(),
    notifications: z.number(),
});
/** The name `projectRegisterSchema` had while a workbench-started project was "personal"; the same schema. */
export const personalProjectCreateSchema = projectRegisterSchema;
/** `PATCH /projects/v1/:id`, owners: rename the project. Policy, catalogue team and area stay administrators' and the portfolio's. */
export const projectOwnerPatchSchema = z
    .object({
    name: z.string().min(1).max(200).optional(),
    classification: classificationSchema.optional(),
    /** AIOE 0.76.0: one line; "" clears it. */
    purpose: z.string().max(PROJECT_PURPOSE_MAX).regex(/^[^\n\r]*$/, "one line").optional(),
})
    .refine((v) => v.name !== undefined || v.classification !== undefined || v.purpose !== undefined, { message: "a name, a classification or a purpose" });
/** `PUT /projects/v1/:id/roots/:rootId/classification`: a repository's own, never weaker than its project's; `null` goes back to inheriting. */
export const projectRootClassificationPutSchema = z.object({ classification: classificationSchema.nullable() });
export const projectMembersPutSchema = z.object({ members: z.array(projectMemberSchema.omit({ email: true, picture: true, linked: true })).max(1000) });
/**
 * Where a team member runs, in a catalogue template: by tags and requirements, never a node id,
 * so a template works for anyone whose assignment reaches a matching node. A person's or a
 * project's saved team may pin a node; a published template may not. AIOE-first shape: mirror
 * the workbench's `TeamMember.runsOn` (src/acp/teamConfig.ts) once it lands.
 */
export const teamPlacementSchema = z
    .object({
    tags: z.array(z.string().min(1).max(64)).max(16).optional(),
    requires: z
        .object({
        runtimes: z.array(z.string()).max(16).optional(),
        models: z.array(z.string()).max(16).optional(),
        minVramGb: z.number().nonnegative().max(10_000).optional(),
    })
        .optional(),
    fallback: z.enum(["local", "wait", "fail"]).optional(),
    /** Run in a node's sandbox (phase 5b). */
    sandbox: z.boolean().optional(),
    /** A workbench (another member's desktop) to run on, by device id; never together with `node`. A saved team only, never a template. */
    device: z.string().min(1).max(100).optional(),
    /** In a provider's cloud (D184, cloudAgents.ts); never with `node` or `device`. A published template may name it: a provider is not a machine. */
    cloud: cloudPlacementSchema.optional(),
})
    .strict();
/**
 * Check a team template's members' placements. Returns problems as sentences; empty when fine.
 * Unknown team shapes pass: the catalogue carries the workbench's TeamConfig opaquely.
 */
export function teamPlacementProblems(content) {
    const members = content?.members;
    if (!Array.isArray(members))
        return [];
    const out = [];
    members.forEach((m, i) => {
        const runsOn = m?.runsOn;
        if (runsOn === undefined)
            return;
        const name = m.name ?? `member ${i + 1}`;
        if (runsOn && typeof runsOn === "object" && ("node" in runsOn || "device" in runsOn)) {
            out.push(`${name}: a template names tags and requirements, not a node or a workbench`);
            return;
        }
        const parsed = teamPlacementSchema.safeParse(runsOn);
        if (!parsed.success)
            out.push(`${name}: ${parsed.error.issues.map((x) => `${x.path.join(".") || "runsOn"} ${x.message}`).join("; ")}`);
    });
    return out;
}
/**
 * A machine that holds one of the project's repositories (AIOE 0.36.0), on `GET /projects/v1/:id`'s `roots[].holders`
 * (optional, no version bump): every device whose latest registration reports the repository, plus a node with a root
 * row for it. `hostUp` is false while its WSL distro, dev container or target is stopped or out of reach (absent when the
 * machine does not report a host); `agents` is what the repository's local board reports, else the machine's busy agents.
 */
export const projectRootHolderSchema = z.object({
    deviceId: z.string(),
    deviceName: z.string(),
    kind: z.enum(["workbench", "node"]),
    /** The person whose workbench it is; absent for a node. */
    owner: z.object({ id: z.string(), name: z.string() }).optional(),
    online: z.boolean(),
    hostUp: z.boolean().optional(),
    lastSeenAt: z.string(),
    agents: z.number().int().nonnegative().optional(),
    distro: z.string().optional(),
});
//# sourceMappingURL=projects.js.map